import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { InputOtpChangeEvent, InputOtpModule } from 'primeng/inputotp';
import { collapse } from '../../../../../../assets/animations/animations';

@Component({
  selector: 'app-platform-tfa-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, FaIconComponent, TranslocoPipe, InputOtpModule],
  templateUrl: './platform-tfa-input.component.html',
  styleUrl: './platform-tfa-input.component.scss',
  animations: [collapse],
})
export class PlatformTfaInputComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject(ElementRef);

  readonly autoSubmit = input(false);
  readonly submit = output<string>();

  readonly icons = { exclamationCircle: faExclamationCircle };

  tfaValue = '';
  validated = false;
  valid = false;

  /**
   * Clears the TOTP field and validation state.
   */
  reset(): void {
    this.validated = false;
    this.valid = false;
    this.tfaValue = '';
    this.cdr.markForCheck();
  }

  /**
   * Focuses the first TOTP digit input.
   */
  focus(): void {
    const focusFirstInput = () => {
      const firstInput = this.host.nativeElement.querySelector(
        '.p-inputotp-input'
      ) as HTMLInputElement | null;
      firstInput?.focus();
    };
    focusFirstInput();
    if (!this.host.nativeElement.querySelector('.p-inputotp-input')) {
      setTimeout(focusFirstInput);
    }
    this.cdr.markForCheck();
  }

  /**
   * Marks the current code as valid or invalid.
   * @param valid - Whether the submitted code was accepted
   */
  validate(valid: boolean): void {
    this.validated = true;
    this.valid = valid;
    if (!valid) {
      this.tfaValue = '';
      this.focus();
    }
    this.cdr.markForCheck();
  }

  /**
   * Handles PrimeNG OTP changes and auto-submits a full code.
   * @param event - OTP change event
   */
  onOtpChange(event: InputOtpChangeEvent): void {
    this.tfaValue = event?.value?.toString?.() ?? String(event?.value ?? '');
    if (this.autoSubmit() && this.tfaValue.length === 6) {
      this.submit.emit(this.tfaValue);
    }
    this.cdr.markForCheck();
  }

  get filled(): boolean {
    return this.tfaValue?.toString().length === 6;
  }
}
